import { CheckIcon, ExternalLink } from 'lucide-react-native';
import { type ReactNode, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { twJoin } from 'tailwind-merge';

import { StatementReferenceHelpRequestRow } from './StatementReferenceHelpRequestRow';
import { truncateStatementText } from './StatementSummary';
import type { CitationRequest, ProposedReference } from '../../model/citationRequest';
import {
  resolveHelpRequestForStatement,
  type ExtractedStatement,
  type StatementReferenceHelpRequest,
} from '../../model/extractedStatements';

import { Box } from '@/components/ui/box';
import { Checkbox, CheckboxIcon, CheckboxIndicator } from '@/components/ui/checkbox';
import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';
import { openReferenceUrl } from '@/util/chat/referenceUtils';
import { formatAuthors } from '@/util/formatAuthors';

/**
 * Extra context the per-statement help-request row needs to render. The
 * citation-request review list doesn't own these values itself — they
 * come from the message's statement references and are passed down by the
 * review surface (wizard or standalone sheet). When this prop is
 * omitted, no help-request rows render (the affordance is gated on
 * the caller supplying this context).
 */
export interface ReviewListHelpRequestContext {
  /** Currently-persisted `statementReferenceHelpRequests` on the parent
   * message. Used to seed each row's initial draft from the persisted
   * entry (if any). The publish handler in
   * `useCitationRequestReviewSheet` re-reads this value for the RMW
   * base, so the list never writes directly. */
  statementReferenceHelpRequests: readonly StatementReferenceHelpRequest[] | undefined;
}

interface CitationRequestReviewListProps {
  citationRequest: CitationRequest;
  /** Parsed `extractedStatements` from the parent message. Used to
   * render a short excerpt under each "STATEMENT N" header so the
   * expert doesn't have to scroll back through the chat to remember
   * what they were citing. */
  extractedStatements: readonly ExtractedStatement[];
  isApproved: (reference: ProposedReference) => boolean;
  onToggle: (referenceId: string) => void;
  isPublishing: boolean;
  /** When supplied, mounts a per-statement
   * `StatementReferenceHelpRequestRow` inside each group's body so the
   * author can request manual help for any statement whose suggested
   * candidates are absent or wrong. Omit to disable help-request rows
   * entirely (e.g. for surfaces that aren't author-side). */
  helpRequestContext?: ReviewListHelpRequestContext;
  /** Bottom padding applied to the scroll content container. Set by
   * callers that overlay a pinned footer (e.g. the wizard's glass
   * action bar) on top of the scroll area so the last list item can
   * scroll above the bar instead of sitting permanently hidden. */
  scrollBottomInset?: number;
  /** Optional trailing slot appended after the last statement group
   * inside the scroll viewport. Used by the wizard to render an
   * in-content destructive secondary action ("Start over") that
   * scrolls with the list rather than competing with the primary CTA
   * in the host chrome. */
  endSlot?: ReactNode;
}

interface StatementGroup {
  statementIndex: number;
  references: ProposedReference[];
}

/**
 * Groups the request's proposed references by `statementIndex` and
 * renders one section per *requested* statement — including statements
 * for which the search returned zero candidates. Each empty group
 * renders a "no references found" subtle message so the expert sees
 * the gap and isn't left wondering why their checked statement
 * disappeared from review.
 *
 * Group headers are sticky inside the scroll viewport so the expert
 * always sees which statement the candidates in view belong to, even
 * when the group's candidate list is taller than the viewport.
 */
export function CitationRequestReviewList({
  citationRequest,
  extractedStatements,
  isApproved,
  onToggle,
  isPublishing,
  helpRequestContext,
  scrollBottomInset,
  endSlot,
}: CitationRequestReviewListProps) {
  const groups = useMemo(
    () => groupByStatement(citationRequest.statementIndices, citationRequest.proposedReferences),
    [citationRequest.statementIndices, citationRequest.proposedReferences]
  );

  const { children, stickyHeaderIndices } = useMemo(
    () =>
      buildScrollChildren({
        groups,
        extractedStatements,
        isApproved,
        onToggle,
        isPublishing,
        messageSid: citationRequest.messageSid,
        helpRequestContext,
      }),
    [
      groups,
      extractedStatements,
      isApproved,
      onToggle,
      isPublishing,
      citationRequest.messageSid,
      helpRequestContext,
    ]
  );

  // 80 clears a 52px sticky footer plus a comfortable gap; 16 keeps
  // the input just above the keyboard when there's no footer.
  const keyboardBottomOffset = scrollBottomInset !== undefined && scrollBottomInset > 0 ? 80 : 16;

  return (
    <KeyboardAwareScrollView
      mode="layout"
      className="w-full flex-1"
      stickyHeaderIndices={stickyHeaderIndices}
      bottomOffset={keyboardBottomOffset}
      contentContainerStyle={
        scrollBottomInset !== undefined ? { paddingBottom: scrollBottomInset } : undefined
      }>
      {children}
      {endSlot}
    </KeyboardAwareScrollView>
  );
}

interface BuildScrollChildrenArgs {
  groups: StatementGroup[];
  extractedStatements: readonly ExtractedStatement[];
  isApproved: (reference: ProposedReference) => boolean;
  onToggle: (referenceId: string) => void;
  isPublishing: boolean;
  messageSid: string;
  helpRequestContext: ReviewListHelpRequestContext | undefined;
}

/**
 * Flattens the grouped reference list into a single child array for
 * the outer `ScrollView`. Each group contributes a sticky header
 * followed by a non-sticky body; `stickyHeaderIndices` is collected
 * alongside so the ScrollView knows which children to pin.
 */
function buildScrollChildren({
  groups,
  extractedStatements,
  isApproved,
  onToggle,
  isPublishing,
  messageSid,
  helpRequestContext,
}: BuildScrollChildrenArgs): { children: ReactNode[]; stickyHeaderIndices: number[] } {
  const children: ReactNode[] = [];

  const stickyHeaderIndices: number[] = [];

  for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 1) {
    const group = groups[groupIndex];

    stickyHeaderIndices.push(children.length);

    children.push(
      <Box key={`header-${group.statementIndex}`} className="bg-background-0 px-4 pb-4 pt-2">
        <ReviewGroupHeader statement={extractedStatements[group.statementIndex]} />
      </Box>
    );

    const helpRequestForStatement = helpRequestContext
      ? resolveHelpRequestForStatement(
          helpRequestContext.statementReferenceHelpRequests,
          group.statementIndex
        )
      : undefined;

    const isLastGroup = groupIndex === groups.length - 1;

    children.push(
      <Box key={`body-${group.statementIndex}`} className="px-4 pb-3">
        <ReviewGroupBody
          group={group}
          isApproved={isApproved}
          onToggle={onToggle}
          isPublishing={isPublishing}
          messageSid={messageSid}
          helpRequest={helpRequestForStatement}
          showHelpRequest={Boolean(helpRequestContext)}
        />
        {!isLastGroup ? <Box className="mt-3" aria-hidden accessibilityElementsHidden /> : null}
      </Box>
    );
  }

  return { children, stickyHeaderIndices };
}

function groupByStatement(
  statementIndices: readonly number[],
  references: readonly ProposedReference[]
): StatementGroup[] {
  const groups = new Map<number, ProposedReference[]>();

  for (const statementIndex of statementIndices) {
    if (!groups.has(statementIndex)) {
      groups.set(statementIndex, []);
    }
  }

  for (const reference of references) {
    const bucket = groups.get(reference.statementIndex) ?? [];

    bucket.push(reference);

    groups.set(reference.statementIndex, bucket);
  }

  return Array.from(groups.entries())
    .map(([statementIndex, refs]) => ({ statementIndex, references: refs }))
    .sort((leftGroup, rightGroup) => leftGroup.statementIndex - rightGroup.statementIndex);
}

interface ReviewGroupHeaderProps {
  /** May be `undefined` when the parent message's extractedStatements
   * have changed since the request was created — defensive only. */
  statement: ExtractedStatement | undefined;
}

/**
 * Sticky header for one statement section. Shows the statement excerpt
 * and (when known) the statement-type badge. The per-group approval
 * count was removed: with the help-request row contributing a parallel
 * reference type, a single "N/M approved" chip no longer maps cleanly
 * to a viewer's mental model of "how many references will publish."
 * The publish CTA in the footer carries the only count.
 */
function ReviewGroupHeader({ statement }: ReviewGroupHeaderProps) {
  const statementText = statement
    ? truncateStatementText(statement.directStatement)
    : 'Statement no longer available';

  return (
    <HStack className="items-stretch gap-3">
      <View className="w-1 shrink-0 self-stretch rounded-full bg-primary-300" />
      <Text
        size="md"
        className={twJoin(
          'min-w-0 flex-1 font-semibold',
          statement ? 'text-typography-900' : 'text-typography-500'
        )}>
        {statementText}
      </Text>
    </HStack>
  );
}

interface ReviewGroupBodyProps {
  group: StatementGroup;
  isApproved: (reference: ProposedReference) => boolean;
  onToggle: (referenceId: string) => void;
  isPublishing: boolean;
  messageSid: string;
  /** Persisted help-request entry for this statement, seeded into the
   * trailing help-request row's draft. `undefined` when none exists. */
  helpRequest: StatementReferenceHelpRequest | undefined;
  /** When true, the trailing `StatementReferenceHelpRequestRow` renders
   * as the last row of this group's list container. */
  showHelpRequest: boolean;
}

/**
 * Renders one statement's candidate references plus (when enabled) the
 * trailing help-request row as a single flush list: a bordered container
 * whose rows are divided by single hairline separators. The help-request
 * row is just the last row in that same list. When a statement has no
 * candidates and no help-request affordance, falls back to a plain
 * "No references found" note with no container.
 */
function ReviewGroupBody({
  group,
  isApproved,
  onToggle,
  isPublishing,
  messageSid,
  helpRequest,
  showHelpRequest,
}: ReviewGroupBodyProps) {
  const hasReferences = group.references.length > 0;

  if (!hasReferences && !showHelpRequest) {
    return (
      <Text size="xs" className="px-1 italic text-typography-500">
        No references found for this statement.
      </Text>
    );
  }

  return (
    <VStack className="overflow-hidden rounded-md border border-outline-100">
      {!hasReferences ? (
        <Text size="xs" className="px-3 py-2 italic text-typography-500">
          No references found for this statement.
        </Text>
      ) : null}
      {group.references.map((reference, index) => (
        <ReviewRow
          key={reference.id}
          reference={reference}
          isApproved={isApproved(reference)}
          onToggle={() => onToggle(reference.id)}
          isPublishing={isPublishing}
          isFirst={index === 0}
        />
      ))}
      {showHelpRequest ? (
        <StatementReferenceHelpRequestRow
          messageSid={messageSid}
          statementIndex={group.statementIndex}
          helpRequest={helpRequest}
          isPublishing={isPublishing}
          isFirst={false}
        />
      ) : null}
    </VStack>
  );
}

interface ReviewRowProps {
  reference: ProposedReference;
  isApproved: boolean;
  onToggle: () => void;
  isPublishing: boolean;
  isFirst: boolean;
}

/**
 * One candidate row in a flush, list-style group: rows sit inside a
 * single bordered container and are separated by one hairline border
 * (`border-t`, skipped on the first row) rather than each being its own
 * card. Composed as two sibling tap targets:
 *
 *  1. The Checkbox (title + authors·journal·year) — tapping anywhere
 *     on this region toggles approve/reject. Approved rows pick up a
 *     `primary-50` tint so the eye can sweep the list and spot the
 *     selected set without landing on each row.
 *  2. The external-link icon — a separate sibling Pressable so opening
 *     the reference for preview doesn't also flip the checkbox.
 *     Without this separation, react-native-web bubbles the URL tap up
 *     to the enclosing Checkbox and the toggle fires too — observed in
 *     the live review sheet during UX review on 2026-05-13.
 */
function ReviewRow({ reference, isApproved, onToggle, isPublishing, isFirst }: ReviewRowProps) {
  const displayTitle = reference.title ?? reference.url;

  const checkedLabel = isApproved ? 'approved' : 'rejected';

  const accessibilityLabel = `${displayTitle}. ${checkedLabel}.`;

  const metaLine = [formatAuthors(reference.authors), reference.journal, reference.year]
    .filter(Boolean)
    .join(' · ');

  const handleOpenUrl = useCallback(() => {
    void openReferenceUrl(reference.url);
  }, [reference.url]);

  return (
    <HStack
      className={twJoin(
        'items-start',
        !isFirst && 'border-t border-outline-100',
        isApproved && 'bg-primary-50'
      )}>
      <Checkbox
        value={`reference-${reference.id}`}
        isChecked={isApproved}
        onChange={onToggle}
        isDisabled={isPublishing}
        aria-label={accessibilityLabel}
        className="flex-1 items-start justify-start gap-2.5 py-2 pl-3">
        <CheckboxIndicator>
          <CheckboxIcon as={CheckIcon} />
        </CheckboxIndicator>
        <VStack className="-mt-0.5 min-w-0 flex-1">
          <Text size="sm" className="text-typography-900">
            <Text size="sm" className="font-medium text-typography-900">
              {displayTitle}
            </Text>
            {metaLine ? (
              <Text size="xs" className="text-typography-600">{` · ${metaLine}`}</Text>
            ) : null}
          </Text>
        </VStack>
      </Checkbox>
      <Pressable
        onPress={handleOpenUrl}
        disabled={isPublishing}
        hitSlop={ICON_BUTTON_HIT_SLOP}
        className="items-center justify-start self-stretch px-3 pt-2 web:cursor-pointer"
        {...getAccessibilityProps({
          role: 'link',
          label: `Open reference: ${displayTitle}`,
          hint: 'Opens the source in an in-app browser',
        })}>
        <ExternalLink className="text-primary-700" size={16} />
      </Pressable>
    </HStack>
  );
}
